import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Menu, X, Phone, Mail, MapPin, ArrowRight, ChevronLeft, ChevronRight, Play, ArrowUpRight } from 'lucide-react';
import CareerCompassLogo from './assets/images/CC.png';
import TeslaLogo from './assets/images/Tesla.png';

/* --- Intersection Observer hook for scroll-triggered reveals --- */
const useReveal = (threshold = 0.15) => {
  const ref = useRef(null);
  const [isVisible, setIsVisible] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) { setIsVisible(true); obs.unobserve(el); } },
      { threshold }
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, [threshold]);
  return [ref, isVisible];
};

/* --- Reveal wrapper component --- */
const Reveal = ({ children, delay = 0, className = '', direction = 'up' }) => {
  const [ref, isVisible] = useReveal(0.12);
  const transforms = {
    up: 'translateY(40px)',
    down: 'translateY(-40px)',
    left: 'translateX(40px)',
    right: 'translateX(-40px)',
    none: 'translateY(0)',
  };
  return (
    <div
      ref={ref}
      className={className}
      style={{
        opacity: isVisible ? 1 : 0,
        transform: isVisible ? 'translate(0,0)' : transforms[direction],
        transition: `opacity 0.8s cubic-bezier(0.22,1,0.36,1) ${delay}s, transform 0.8s cubic-bezier(0.22,1,0.36,1) ${delay}s`,
      }}
    >
      {children}
    </div>
  );
};

/* --- Exam detail data --- */
const examDetails = {
  "JEE Main": {
    fullName: "Joint Entrance Examination Main",
    conductedBy: "National Testing Agency (NTA)",
    eligibility: "Class 12 passed/appearing with PCM. Age: No upper limit.",
    pattern: "90 questions (MCQ + Numerical), 300 marks, 3 hours",
    topColleges: "NITs, IIITs, GFTIs, and other CFTIs",
    syllabus: "Physics, Chemistry, Mathematics (Class 11 & 12 NCERT)",
    attempts: "6 attempts over 3 consecutive years",
    mode: "Computer Based Test (CBT)"
  },
  "JEE Advanced": {
    fullName: "Joint Entrance Examination Advanced",
    conductedBy: "One of the 7 Zonal IITs (Rotational basis)",
    eligibility: "Must qualify JEE Main. Top 2,50,000 candidates eligible.",
    pattern: "Two papers (Paper 1 & 2), 3 hours each",
    topColleges: "23 IITs across India",
    syllabus: "Physics, Chemistry, Mathematics (Advanced level)",
    attempts: "Maximum 2 attempts in consecutive years",
    mode: "Computer Based Test (CBT)"
  },
  "BITSAT": {
    fullName: "Birla Institute of Technology & Science Admission Test",
    conductedBy: "BITS Pilani",
    eligibility: "Class 12 with PCM, min 75% aggregate",
    pattern: "130 questions, 390 marks, 3 hours (MCQ only)",
    topColleges: "BITS Pilani, BITS Goa, BITS Hyderabad",
    syllabus: "Physics, Chemistry, Maths, English, Logical Reasoning",
    attempts: "Once per year",
    mode: "Computer Based Test (CBT)"
  },
  "VITEEE": {
    fullName: "VIT Engineering Entrance Examination",
    conductedBy: "Vellore Institute of Technology",
    eligibility: "Class 12 with PCM/PCB, min 60% aggregate",
    pattern: "125 questions, 125 marks, 2.5 hours",
    topColleges: "VIT Vellore, VIT Chennai, VIT Bhopal, VIT AP",
    syllabus: "Physics, Chemistry, Mathematics/Biology, Aptitude",
    attempts: "Once per year",
    mode: "Computer Based Test (CBT)"
  },
  "GATE": {
    fullName: "Graduate Aptitude Test in Engineering",
    conductedBy: "IISc Bangalore & 7 IITs (jointly)",
    eligibility: "Bachelor's degree in Engineering/Technology or equivalent",
    pattern: "65 questions, 100 marks, 3 hours",
    topColleges: "IITs, IISc, NITs, PSU recruitment",
    syllabus: "Subject-specific (29 papers available)",
    attempts: "No limit on attempts",
    mode: "Computer Based Test (CBT)"
  },
  "PGCET": {
    fullName: "Post Graduate Common Entrance Test",
    conductedBy: "Karnataka Examinations Authority (KEA)",
    eligibility: "Bachelor's degree in relevant discipline",
    pattern: "Varies by subject, typically MCQ format",
    topColleges: "Engineering colleges in Karnataka",
    syllabus: "Subject-specific based on UG curriculum",
    attempts: "Once per year",
    mode: "Computer Based Test / Pen & Paper"
  },
  "NATA": {
    fullName: "National Aptitude Test in Architecture",
    conductedBy: "Council of Architecture (CoA)",
    eligibility: "Class 12 with Mathematics, min 50%",
    pattern: "Drawing + MCQ, 200 marks, 3 hours",
    topColleges: "SPA, CEPT, JJ School of Architecture",
    syllabus: "Drawing, Aptitude, Mathematics, General Knowledge",
    attempts: "3 attempts per year",
    mode: "Online + Drawing on paper"
  },
  "JEE Main Paper 2": {
    fullName: "JEE Main Paper 2 (B.Arch/B.Planning)",
    conductedBy: "National Testing Agency (NTA)",
    eligibility: "Class 12 passed/appearing with Mathematics",
    pattern: "Part I: Maths + Aptitude (MCQ), Part II: Drawing",
    topColleges: "NITs, SPAs, and CFTIs for B.Arch/B.Planning",
    syllabus: "Mathematics, Aptitude, Drawing",
    attempts: "6 attempts over 3 consecutive years",
    mode: "CBT + Drawing on paper"
  },
  "NID DAT": {
    fullName: "National Institute of Design - Design Aptitude Test",
    conductedBy: "National Institute of Design (NID)",
    eligibility: "Class 12 passed for B.Des; Graduation for M.Des",
    pattern: "Prelims (MCQ) + Mains (Studio test)",
    topColleges: "NID Ahmedabad, NID Gandhinagar, NID Haryana",
    syllabus: "Design aptitude, creativity, observation, logic",
    attempts: "Once per year",
    mode: "Online Prelims + Offline Mains"
  },
  "UCEED": {
    fullName: "Undergraduate Common Entrance Exam for Design",
    conductedBy: "IIT Bombay (jointly with IIT Delhi, IIT Guwahati)",
    eligibility: "Class 12 passed/appearing, age <= 24 years",
    pattern: "Part A (MCQ) + Part B (Drawing), 3 hours",
    topColleges: "IIT Bombay, IIT Delhi, IIT Guwahati, IIITDM Jabalpur",
    syllabus: "Visualization, observation, design thinking, drawing",
    attempts: "Maximum 2 attempts",
    mode: "Computer Based + Drawing"
  },
  "CEED": {
    fullName: "Common Entrance Exam for Design",
    conductedBy: "IIT Bombay",
    eligibility: "Bachelor's degree in any discipline",
    pattern: "Part A (MCQ) + Part B (Drawing/Design), 3 hours",
    topColleges: "IIT Bombay, IISc Bangalore, IIT Delhi, IIT Kanpur",
    syllabus: "Design aptitude, creativity, visualization, problem-solving",
    attempts: "No limit on attempts",
    mode: "Computer Based + Offline Drawing"
  },
  "CAT": {
    fullName: "Common Admission Test",
    conductedBy: "IIMs (rotational basis)",
    eligibility: "Bachelor's degree with min 50% (45% for SC/ST)",
    pattern: "66 questions, 198 marks, 2 hours (3 sections)",
    topColleges: "IIM Ahmedabad, IIM Bangalore, IIM Calcutta, IIM Lucknow",
    syllabus: "VARC, DILR, Quantitative Aptitude",
    attempts: "No limit on attempts",
    mode: "Computer Based Test (CBT)"
  },
  "XAT": {
    fullName: "Xavier Aptitude Test",
    conductedBy: "XLRI Jamshedpur",
    eligibility: "Bachelor's degree (any discipline), final year eligible",
    pattern: "100 questions, 3 hours + Essay writing 25 min",
    topColleges: "XLRI, XIMB, IMT Ghaziabad, GIM Goa",
    syllabus: "VARC, Decision Making, QA & DI",
    attempts: "Once per year",
    mode: "Computer Based Test (CBT)"
  },
  "MAT": {
    fullName: "Management Aptitude Test",
    conductedBy: "AIMA (All India Management Association)",
    eligibility: "Bachelor's degree in any discipline",
    pattern: "200 questions, 200 marks, 2.5 hours",
    topColleges: "600+ B-schools across India",
    syllabus: "Language, Maths, Data Analysis, Intelligence, Indian & Global Environment",
    attempts: "4 times a year",
    mode: "Online (IBT) / Paper Based (PBT)"
  },
  "SNAP": {
    fullName: "Symbiosis National Aptitude Test",
    conductedBy: "Symbiosis International University",
    eligibility: "Graduation with min 50% (45% for SC/ST)",
    pattern: "60 questions, 60 marks, 1 hour",
    topColleges: "SIBM Pune, SCMHRD, SIIB, SITM",
    syllabus: "General English, Quantitative Aptitude, Analytical & Logical Reasoning",
    attempts: "Once per year (3 test windows)",
    mode: "Computer Based Test (CBT)"
  },
  "NMAT": {
    fullName: "NMAT by GMAC",
    conductedBy: "Graduate Management Admission Council (GMAC)",
    eligibility: "Bachelor's degree (any discipline), final year eligible",
    pattern: "108 questions, 360 marks, 2 hours",
    topColleges: "NMIMS Mumbai, NMIMS Bangalore, NMIMS Hyderabad",
    syllabus: "Language Skills, Quantitative Skills, Logical Reasoning",
    attempts: "3 attempts per registration cycle",
    mode: "Computer Based Test (CBT)"
  },
  "CLAT": {
    fullName: "Common Law Admission Test",
    conductedBy: "Consortium of NLUs",
    eligibility: "Class 12 passed (UG) / LLB degree (PG), no age limit",
    pattern: "150 questions, 150 marks, 2 hours (UG)",
    topColleges: "NLSIU Bangalore, NALSAR, NLU Delhi, NUJS Kolkata",
    syllabus: "English, Current Affairs, Legal Reasoning, Logical Reasoning, Quantitative Techniques",
    attempts: "No limit on attempts",
    mode: "Offline (Pen & Paper)"
  },
  "AILET": {
    fullName: "All India Law Entrance Test",
    conductedBy: "National Law University Delhi",
    eligibility: "Class 12 passed (UG) / LLB degree (PG)",
    pattern: "150 questions, 150 marks, 1.5 hours",
    topColleges: "NLU Delhi",
    syllabus: "English, General Knowledge, Legal Aptitude, Reasoning, Mathematics",
    attempts: "No limit on attempts",
    mode: "Offline (Pen & Paper)"
  },
  "LSAT India": {
    fullName: "Law School Admission Test - India",
    conductedBy: "Pearson VUE (licensed by LSAC)",
    eligibility: "Class 12 passed/appearing",
    pattern: "92-100 questions, no negative marking, 2 hours 20 min",
    topColleges: "Jindal Global Law School, UPES, Alliance University",
    syllabus: "Analytical Reasoning, Logical Reasoning, Reading Comprehension",
    attempts: "No limit",
    mode: "Online proctored / Test center"
  },
  "DU LLB": {
    fullName: "Delhi University LLB Entrance Test",
    conductedBy: "NTA on behalf of Delhi University",
    eligibility: "Graduation with min 50% (45% for SC/ST)",
    pattern: "MCQ format, 2 hours",
    topColleges: "Faculty of Law, Delhi University",
    syllabus: "English, General Knowledge, Analytical Ability, Legal Awareness",
    attempts: "Once per year",
    mode: "Computer Based Test (CBT)"
  },
  "BHU UET": {
    fullName: "Banaras Hindu University Undergraduate Entrance Test",
    conductedBy: "NTA on behalf of BHU",
    eligibility: "Class 12 passed (varies by course)",
    pattern: "MCQ format, varies by course, 2.5 hours",
    topColleges: "Banaras Hindu University (all faculties)",
    syllabus: "Course-specific subjects based on UG program",
    attempts: "Once per year",
    mode: "Computer Based Test (CBT)"
  },
  "CUET UG": {
    fullName: "Common University Entrance Test - Undergraduate",
    conductedBy: "National Testing Agency (NTA)",
    eligibility: "Class 12 passed/appearing from recognized board",
    pattern: "MCQ, up to 3 hours (varies by subjects chosen)",
    topColleges: "DU, JNU, BHU, Jamia Millia, and 200+ central/state universities",
    syllabus: "Language, Domain subjects (Class 12), General Test",
    attempts: "Once per year",
    mode: "Computer Based Test (CBT)"
  },
  "CUET PG": {
    fullName: "Common University Entrance Test - Postgraduate",
    conductedBy: "National Testing Agency (NTA)",
    eligibility: "Bachelor's degree in relevant discipline",
    pattern: "MCQ format, 75 questions, 1.75 hours per paper",
    topColleges: "DU, JNU, BHU, and 200+ central/state universities",
    syllabus: "Domain-specific based on PG program chosen",
    attempts: "Once per year",
    mode: "Computer Based Test (CBT)"
  },
  "IPU CET": {
    fullName: "Indraprastha University Common Entrance Test",
    conductedBy: "Guru Gobind Singh Indraprastha University",
    eligibility: "Class 12 / Graduation (varies by course)",
    pattern: "MCQ format, varies by program",
    topColleges: "GGSIPU affiliated colleges in Delhi NCR",
    syllabus: "Subject-specific based on chosen program",
    attempts: "Once per year",
    mode: "Computer Based Test (CBT)"
  },
  "DUET": {
    fullName: "Delhi University Entrance Test",
    conductedBy: "NTA on behalf of Delhi University",
    eligibility: "Bachelor's degree in relevant discipline (for PG)",
    pattern: "MCQ format, 2 hours",
    topColleges: "Delhi University (all PG departments)",
    syllabus: "Domain-specific based on PG program",
    attempts: "Once per year",
    mode: "Computer Based Test (CBT)"
  },
  "JNU Entrance": {
    fullName: "Jawaharlal Nehru University Entrance Examination",
    conductedBy: "NTA on behalf of JNU",
    eligibility: "Bachelor's degree (for PG), Master's (for M.Phil/PhD)",
    pattern: "MCQ format, 2 hours per paper",
    topColleges: "Jawaharlal Nehru University, New Delhi",
    syllabus: "Domain-specific based on chosen school/centre",
    attempts: "Once per year",
    mode: "Computer Based Test (CBT)"
  },
};

const CareerCompassWebsite = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isContactModalOpen, setIsContactModalOpen] = useState(false);
  const [isExamModalOpen, setIsExamModalOpen] = useState(false);
  const [selectedExam, setSelectedExam] = useState(null);
  const [selectedExamCategory, setSelectedExamCategory] = useState('');
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isVideoPlaying, setIsVideoPlaying] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    grade: '',
    school: '',
    contact: ''
  });

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const examCategories = {
    engineering: {
      title: "Engineering",
      subtitle: "Entrance Exams",
      accent: "#E84C3D",
      label: "ENG",
      exams: [
        { name: "JEE Main", date: "January 2025", type: "UG", status: "Open" },
        { name: "JEE Advanced", date: "May 2025", type: "UG", status: "Upcoming" },
        { name: "BITSAT", date: "May 2025", type: "UG", status: "Upcoming" },
        { name: "VITEEE", date: "April 2025", type: "UG", status: "Upcoming" },
        { name: "GATE", date: "February 2025", type: "PG", status: "Open" },
        { name: "PGCET", date: "March 2025", type: "PG", status: "Upcoming" }
      ]
    },
    design: {
      title: "Design",
      subtitle: "& Architecture",
      accent: "#D4A853",
      label: "DES",
      exams: [
        { name: "NATA", date: "April 2025", type: "UG", status: "Upcoming" },
        { name: "JEE Main Paper 2", date: "January 2025", type: "UG", status: "Open" },
        { name: "NID DAT", date: "January 2025", type: "UG/PG", status: "Open" },
        { name: "UCEED", date: "January 2025", type: "UG", status: "Open" },
        { name: "CEED", date: "January 2025", type: "PG", status: "Open" }
      ]
    },
    management: {
      title: "Management",
      subtitle: "Studies",
      accent: "#2E7D6F",
      label: "MGT",
      exams: [
        { name: "CAT", date: "November 2024", type: "PG", status: "Completed" },
        { name: "XAT", date: "January 2025", type: "PG", status: "Open" },
        { name: "MAT", date: "February 2025", type: "PG", status: "Upcoming" },
        { name: "SNAP", date: "December 2024", type: "PG", status: "Completed" },
        { name: "NMAT", date: "October 2024", type: "PG", status: "Completed" }
      ]
    },
    law: {
      title: "Law",
      subtitle: "& Legal Studies",
      accent: "#8B5E3C",
      label: "LAW",
      exams: [
        { name: "CLAT", date: "December 2024", type: "UG/PG", status: "Completed" },
        { name: "AILET", date: "December 2024", type: "UG/PG", status: "Completed" },
        { name: "LSAT India", date: "January 2025", type: "UG", status: "Open" },
        { name: "DU LLB", date: "June 2025", type: "UG", status: "Upcoming" },
        { name: "BHU UET", date: "May 2025", type: "UG/PG", status: "Upcoming" }
      ]
    },
    arts: {
      title: "Arts",
      subtitle: "& Sciences",
      accent: "#6B4C8A",
      label: "ART",
      exams: [
        { name: "CUET UG", date: "May 2025", type: "UG", status: "Upcoming" },
        { name: "CUET PG", date: "June 2025", type: "PG", status: "Upcoming" },
        { name: "IPU CET", date: "April 2025", type: "UG/PG", status: "Upcoming" },
        { name: "DUET", date: "May 2025", type: "PG", status: "Upcoming" },
        { name: "JNU Entrance", date: "April 2025", type: "PG", status: "Upcoming" }
      ]
    }
  };

  const heroSlides = [
    {
      image: "https://images.unsplash.com/photo-1523240795612-9a054b0db644?w=1920&h=1080&fit=crop",
      title: "Navigate Your Career Journey",
      subtitle: "Bridge the gap between education and future career readiness with personalized guidance.",
    },
    {
      image: "https://images.unsplash.com/photo-1571260899304-425eee4c7efc?w=1920&h=1080&fit=crop",
      title: "Excel in Entrance Exams",
      subtitle: "Targeted coaching across Engineering, Law, Management & more \u2014 all under one roof.",
    },
    {
      image: "https://images.unsplash.com/photo-1606761568499-6d2451b23c66?w=1920&h=1080&fit=crop",
      title: "Dream Bigger, Plan Smarter",
      subtitle: "Personalized career mapping with the combined expertise of Career Compass & Tesla's Academy.",
    }
  ];

  const successStories = [
    {
      name: "Arjun Sharma",
      achievement: "IIT Bombay \u2014 Computer Science",
      quote: "Career Compass helped me discover my true potential and guided me to my dream college.",
      exam: "JEE Advanced",
    },
    {
      name: "Priya Patel",
      achievement: "NLSIU Bangalore \u2014 Law",
      quote: "The personalized career mapping changed my perspective completely. I found my calling.",
      exam: "CLAT",
    },
    {
      name: "Rohit Kumar",
      achievement: "IIM Calcutta \u2014 MBA",
      quote: "From average student to IIM graduate \u2014 Career Compass made it possible with structured planning.",
      exam: "CAT",
    }
  ];

  const statsData = [
    { number: "95%", label: "Success Rate" },
    { number: "2000+", label: "Students Guided" },
    { number: "50+", label: "Entrance Exams" },
    { number: "15+", label: "Years Experience" }
  ];

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % heroSlides.length);
    }, 6000);
    return () => clearInterval(interval);
  }, [heroSlides.length]);

  const openExamModal = useCallback((exam, categoryTitle) => {
    setSelectedExam(exam);
    setSelectedExamCategory(categoryTitle);
    setIsExamModalOpen(true);
  }, []);

  const handleSubmit = () => {
    if (!formData.name || !formData.grade || !formData.school || !formData.contact) {
      alert('Please fill in all fields');
      return;
    }
    const message = "Hello! I'm interested in Career Compass programs.\n\nName: " + formData.name + "\nGrade: " + formData.grade + "\nSchool/College: " + formData.school + "\nContact: " + formData.contact + "\n\nPlease provide more information about your courses.";
    const whatsappUrl = "https://wa.me/919626137666?text=" + encodeURIComponent(message);
    window.open(whatsappUrl, '_blank');
    setIsContactModalOpen(false);
    setFormData({ name: '', grade: '', school: '', contact: '' });
  };

  const handleExamEnquiry = () => {
    if (!formData.name || !formData.grade || !formData.school || !formData.contact) {
      alert('Please fill in all fields');
      return;
    }
    const details = examDetails[selectedExam?.name] || {};
    const message = "Hello! I'd like to enquire about *" + selectedExam?.name + "* (" + selectedExamCategory + ").\n\nExam: " + selectedExam?.name + "\nFull Name: " + (details.fullName || selectedExam?.name) + "\nDate: " + selectedExam?.date + "\nType: " + selectedExam?.type + "\nStatus: " + selectedExam?.status + "\n\n--- Student Details ---\nName: " + formData.name + "\nGrade: " + formData.grade + "\nSchool/College: " + formData.school + "\nContact: " + formData.contact + "\n\nPlease share coaching details and fees for this exam.";
    const whatsappUrl = "https://wa.me/919626137666?text=" + encodeURIComponent(message);
    window.open(whatsappUrl, '_blank');
    setIsExamModalOpen(false);
    setFormData({ name: '', grade: '', school: '', contact: '' });
  };

  const handleCertificationEnquiry = () => {
    const message = "Hello! I'm interested in the *Certified Career Counselor Program* by Career Compass.\n\nI'd like to know more about:\n- Training schedule & fees\n- Certification details\n- Placement support\n\nPlease share the complete details.";
    const whatsappUrl = "https://wa.me/919626137666?text=" + encodeURIComponent(message);
    window.open(whatsappUrl, '_blank');
  };

  const scrollToSection = (sectionId) => {
    document.getElementById(sectionId)?.scrollIntoView({ behavior: 'smooth' });
    setIsMenuOpen(false);
  };

  const nextSlide = () => setCurrentSlide((p) => (p + 1) % heroSlides.length);
  const prevSlide = () => setCurrentSlide((p) => (p - 1 + heroSlides.length) % heroSlides.length);

  const statusColor = (s) => {
    if (s === 'Open') return { bg: 'bg-emerald-50', text: 'text-emerald-700', border: 'border-emerald-200', dot: 'bg-emerald-500' };
    if (s === 'Upcoming') return { bg: 'bg-amber-50', text: 'text-amber-700', border: 'border-amber-200', dot: 'bg-amber-500' };
    return { bg: 'bg-gray-50', text: 'text-gray-500', border: 'border-gray-200', dot: 'bg-gray-400' };
  };

  const WhatsAppIcon = () => (
    <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347z" /><path d="M12 0C5.373 0 0 5.373 0 12c0 2.625.846 5.059 2.284 7.034L.789 23.492a.75.75 0 00.917.918l4.462-1.494A11.945 11.945 0 0012 24c6.627 0 12-5.373 12-12S18.627 0 12 0zm0 22c-2.24 0-4.31-.726-5.993-1.955l-.42-.313-2.648.886.886-2.642-.324-.433A9.932 9.932 0 012 12C2 6.477 6.477 2 12 2s10 4.477 10 10-4.477 10-10 10z" /></svg>
  );

  /* --- Form field renderer --- */
  const FormFields = ({ onSubmit, buttonText, buttonColor = "#25D366" }) => (
    <div className="space-y-3">
      {[
        { label: "Full Name", type: "text", placeholder: "Enter your full name", key: "name" },
        { label: "Grade / Class", type: "select", key: "grade", options: ["", "Class 10", "Class 11", "Class 12", "Graduate", "Post Graduate"] },
        { label: "School / College", type: "text", placeholder: "Your institution name", key: "school" },
        { label: "Contact Number", type: "tel", placeholder: "+91 XXXXX XXXXX", key: "contact" }
      ].map((field) => (
        <div key={field.key}>
          <label className="block text-xs font-semibold text-gray-400 mb-1 tracking-wider uppercase" style={{ fontSize: '10px' }}>{field.label}</label>
          {field.type === "select" ? (
            <select
              value={formData[field.key]}
              onChange={(e) => setFormData({ ...formData, [field.key]: e.target.value })}
              className="w-full px-3 py-2.5 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-blue-900/20 focus:border-blue-900 transition-all duration-300 bg-white outline-none"
            >
              {field.options.map((o) => <option key={o} value={o}>{o || "Select Grade"}</option>)}
            </select>
          ) : (
            <input
              type={field.type}
              value={formData[field.key]}
              onChange={(e) => setFormData({ ...formData, [field.key]: e.target.value })}
              className="w-full px-3 py-2.5 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-blue-900/20 focus:border-blue-900 transition-all duration-300 bg-white outline-none"
              placeholder={field.placeholder}
            />
          )}
        </div>
      ))}
      <button
        onClick={onSubmit}
        className="w-full py-3 text-white rounded-lg font-semibold hover:opacity-90 transition-all duration-300 flex items-center justify-center gap-2 mt-1"
        style={{ backgroundColor: buttonColor }}
      >
        <WhatsAppIcon />
        {buttonText}
      </button>
    </div>
  );

  return (
    <div className="min-h-screen bg-white">

      {/* === HEADER === */}
      <header className={"fixed top-0 w-full z-50 transition-all duration-500 " + (scrolled ? "bg-white/95 backdrop-blur-md shadow-sm" : "bg-transparent")}>
        <div className="max-w-7xl mx-auto px-5 sm:px-8 lg:px-10">
          <div className="flex justify-between items-center py-3">
            <div className="flex items-center gap-4">
              <img src={CareerCompassLogo} className="w-14 h-14 sm:w-16 sm:h-16" alt="Career Compass" />
              <div className="hidden sm:block">
                <span className={"text-lg font-bold tracking-tight transition-colors duration-500 " + (scrolled ? "text-blue-950" : "text-white")}>Career Compass</span>
                <p className="text-xs font-semibold tracking-widest uppercase text-red-500">We Navigate</p>
              </div>
              <div className="hidden lg:flex items-center gap-4 ml-4 pl-4" style={{ borderLeft: '1px solid ' + (scrolled ? 'rgba(27,42,91,0.12)' : 'rgba(255,255,255,0.25)') }}>
                <img src={TeslaLogo} className="w-12 h-12" alt="Tesla's Academy" />
                <div>
                  <span className={"text-sm font-semibold transition-colors duration-500 " + (scrolled ? "text-blue-950" : "text-white")}>Tesla's Academy</span>
                  <p className={"text-xs tracking-wider uppercase transition-colors duration-500 " + (scrolled ? "text-gray-400" : "text-white/60")} style={{ fontSize: '10px' }}>Knowledge is Power</p>
                </div>
              </div>
            </div>

            <nav className="hidden md:flex items-center gap-1">
              {['Home', 'About', 'Programs', 'Exams', 'Certify', 'Contact'].map((item) => (
                <button key={item} onClick={() => scrollToSection(item === 'Certify' ? 'certification' : item.toLowerCase())} className={"px-4 py-2 text-sm font-medium rounded-full transition-all duration-300 " + (scrolled ? "text-gray-500 hover:text-blue-950 hover:bg-gray-50" : "text-white/80 hover:text-white hover:bg-white/10")}>{item}</button>
              ))}
              <button onClick={() => setIsContactModalOpen(true)} className="ml-3 px-5 py-2.5 bg-red-500 text-white text-sm font-semibold rounded-full hover:bg-red-600 transition-all duration-300 hover:shadow-lg">Enquire Now</button>
            </nav>

            <button onClick={() => setIsMenuOpen(!isMenuOpen)} className={"md:hidden p-2.5 rounded-xl transition-all duration-300 " + (scrolled ? "text-blue-950 hover:bg-gray-100" : "text-white hover:bg-white/10")}>
              {isMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>

          <div className={"md:hidden overflow-hidden transition-all duration-500 ease-in-out " + (isMenuOpen ? "max-h-96 opacity-100 pb-6" : "max-h-0 opacity-0")}>
            <nav className="flex flex-col gap-1 pt-4 border-t border-gray-100">
              {['Home', 'About', 'Programs', 'Exams', 'Certify', 'Contact'].map((item) => (
                <button key={item} onClick={() => scrollToSection(item === 'Certify' ? 'certification' : item.toLowerCase())} className="text-left py-3 px-4 text-blue-950 hover:bg-gray-50 rounded-xl font-medium transition-all duration-300">{item}</button>
              ))}
              <button onClick={() => { setIsContactModalOpen(true); setIsMenuOpen(false); }} className="mt-2 py-3 px-4 bg-red-500 text-white rounded-xl font-semibold text-center">Enquire Now</button>
            </nav>
          </div>
        </div>
      </header>

      {/* === HERO === */}
      <section id="home" className="relative h-screen overflow-hidden">
        <div className="absolute inset-0">
          {heroSlides.map((slide, index) => (
            <div key={index} className="absolute inset-0" style={{ opacity: index === currentSlide ? 1 : 0, transition: 'opacity 1.5s cubic-bezier(0.22,1,0.36,1)' }}>
              <img src={slide.image} alt={slide.title} className="w-full h-full object-cover" style={{ transform: index === currentSlide ? 'scale(1.05)' : 'scale(1)', transition: 'transform 8s ease-out' }} />
              <div className="absolute inset-0" style={{ backgroundColor: 'rgba(27,42,91,0.75)' }}></div>
            </div>
          ))}
        </div>

        <div className="relative z-10 h-full flex items-center">
          <div className="max-w-7xl mx-auto px-5 sm:px-8 lg:px-10 w-full">
            <div className="max-w-3xl">
              <div className="flex items-center gap-3 mb-8">
                <div className="w-12 h-0.5 bg-red-500"></div>
                <span className="text-red-400 text-sm font-semibold tracking-widest uppercase">India's Premier Career Navigation Platform</span>
              </div>
              <h1 className="text-4xl sm:text-5xl lg:text-7xl font-bold text-white leading-tight mb-6 tracking-tight">{heroSlides[currentSlide].title}</h1>
              <p className="text-lg sm:text-xl text-white/70 mb-10 leading-relaxed max-w-2xl font-light">{heroSlides[currentSlide].subtitle}</p>
              <div className="flex flex-col sm:flex-row gap-4">
                <button onClick={() => scrollToSection('programs')} className="group px-8 py-4 bg-red-500 text-white rounded-full font-semibold text-base hover:bg-red-600 transition-all duration-300 hover:shadow-xl flex items-center justify-center gap-2">
                  Explore Programs
                  <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform duration-300" />
                </button>
                <button onClick={() => setIsContactModalOpen(true)} className="px-8 py-4 border border-white/30 text-white rounded-full font-semibold text-base hover:bg-white/10 backdrop-blur-sm transition-all duration-300">Get Free Counseling</button>
              </div>
            </div>
          </div>
        </div>

        <button onClick={prevSlide} className="absolute left-5 top-1/2 -translate-y-1/2 z-10 w-12 h-12 rounded-full bg-white/10 backdrop-blur-sm text-white flex items-center justify-center hover:bg-white/20 transition-all duration-300 border border-white/10"><ChevronLeft className="w-5 h-5" /></button>
        <button onClick={nextSlide} className="absolute right-5 top-1/2 -translate-y-1/2 z-10 w-12 h-12 rounded-full bg-white/10 backdrop-blur-sm text-white flex items-center justify-center hover:bg-white/20 transition-all duration-300 border border-white/10"><ChevronRight className="w-5 h-5" /></button>

        <div className="absolute bottom-10 left-1/2 -translate-x-1/2 flex gap-3 z-10">
          {heroSlides.map((_, i) => (
            <button key={i} onClick={() => setCurrentSlide(i)} className={"rounded-full transition-all duration-500 " + (i === currentSlide ? "w-10 h-2 bg-red-500" : "w-2 h-2 bg-white/40 hover:bg-white/60")} />
          ))}
        </div>
      </section>

      {/* === STATS BAR === */}
      <section className="relative z-20 -mt-16">
        <div className="max-w-6xl mx-auto px-5 sm:px-8">
          <Reveal>
            <div className="bg-white rounded-2xl shadow-xl border border-gray-100">
              <div className="grid grid-cols-2 md:grid-cols-4 divide-x divide-gray-100">
                {statsData.map((stat, index) => (
                  <div key={index} className="py-8 px-6 text-center group">
                    <h3 className="text-3xl sm:text-4xl font-bold text-blue-950 mb-1 group-hover:text-red-500 transition-colors duration-500">{stat.number}</h3>
                    <p className="text-sm text-gray-500 font-medium">{stat.label}</p>
                  </div>
                ))}
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      {/* === ABOUT === */}
      <section id="about" className="py-28 bg-white">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 lg:px-10">
          <div className="grid lg:grid-cols-2 gap-16 lg:gap-24 items-center">
            <Reveal direction="left">
              <div className="relative">
                <div className="absolute -top-6 -left-6 w-full h-full rounded-2xl" style={{ backgroundColor: 'rgba(27,42,91,0.04)' }}></div>
                <div className="absolute -bottom-6 -right-6 w-32 h-32 border-2 rounded-2xl" style={{ borderColor: 'rgba(232,76,61,0.15)' }}></div>
                <img src="https://images.unsplash.com/photo-1522202176988-66273c2fd55f?w=600&h=500&fit=crop" alt="Students collaborating" className="relative rounded-2xl shadow-2xl w-full object-cover" style={{ aspectRatio: '5/4' }} />
              </div>
            </Reveal>

            <div>
              <Reveal delay={0.1}>
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-0.5 bg-red-500"></div>
                  <span className="text-red-500 text-sm font-semibold tracking-widest uppercase">About Us</span>
                </div>
                <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-blue-950 leading-tight mb-6 tracking-tight">
                  Transforming Dreams<br /><span className="text-red-500">Into Reality</span>
                </h2>
                <p className="text-gray-600 text-lg leading-relaxed mb-8">We are committed to inspiring every student to dream bigger, plan smarter, and prepare for life beyond academics. Through personalized assessments, career mapping and targeted entrance exam coaching, we equip students with the clarity, skills, and strategies they need.</p>
              </Reveal>

              <Reveal delay={0.25}>
                <div className="space-y-5">
                  {[
                    { title: "Personalized Assessments", desc: "Scientific evaluation to discover your true potential and career interests." },
                    { title: "Strategic Career Mapping", desc: "Aligning your academic journey with long-term career goals." },
                    { title: "Entrance Exam Coaching", desc: "Targeted preparation for competitive examinations across all streams." }
                  ].map((item, i) => (
                    <div key={i} className="flex gap-4 group">
                      <div className="flex-shrink-0 w-10 h-10 rounded-lg flex items-center justify-center group-hover:bg-red-50 transition-colors duration-300" style={{ backgroundColor: 'rgba(27,42,91,0.04)' }}>
                        <span className="text-blue-950 font-bold text-sm group-hover:text-red-500 transition-colors duration-300">{String(i + 1).padStart(2, '0')}</span>
                      </div>
                      <div>
                        <h4 className="font-semibold text-blue-950 mb-0.5">{item.title}</h4>
                        <p className="text-gray-500 text-sm leading-relaxed">{item.desc}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </Reveal>
            </div>
          </div>
        </div>
      </section>

      {/* === PROGRAMS === */}
      <section id="programs" className="py-28" style={{ backgroundColor: '#FAFBFD' }}>
        <div className="max-w-7xl mx-auto px-5 sm:px-8 lg:px-10">
          <Reveal>
            <div className="text-center mb-16">
              <div className="flex items-center justify-center gap-3 mb-4">
                <div className="w-10 h-0.5 bg-red-500"></div>
                <span className="text-red-500 text-sm font-semibold tracking-widest uppercase">Our Programs</span>
                <div className="w-10 h-0.5 bg-red-500"></div>
              </div>
              <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-blue-950 tracking-tight mb-4">Excellence Across Every Stream</h2>
              <p className="text-gray-500 text-lg max-w-xl mx-auto">Comprehensive coaching across five major academic streams with expert faculty and proven results.</p>
            </div>
          </Reveal>

          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {Object.entries(examCategories).map(([key, cat], index) => (
              <Reveal key={key} delay={index * 0.08}>
                <div onClick={() => scrollToSection('exams')} className="group cursor-pointer bg-white rounded-2xl border border-gray-100 overflow-hidden hover:shadow-xl transition-all duration-500 hover:-translate-y-1">
                  <div className="p-7">
                    <div className="inline-flex items-center px-3 py-1 rounded-md text-xs font-bold tracking-wider mb-5" style={{ backgroundColor: cat.accent + '12', color: cat.accent }}>{cat.label}</div>
                    <h3 className="text-xl font-bold text-blue-950 mb-1 group-hover:text-red-500 transition-colors duration-300">{cat.title}<span className="font-normal text-gray-400 ml-1">{cat.subtitle}</span></h3>
                    <p className="text-gray-500 text-sm mt-3 mb-5">{cat.exams.length} entrance exams including {cat.exams[0]?.name}, {cat.exams[1]?.name}</p>
                    <div className="flex items-center gap-2 text-red-500 font-semibold text-sm group-hover:gap-3 transition-all duration-300">View Exams<ArrowRight className="w-4 h-4" /></div>
                  </div>
                  <div className="h-1 w-0 group-hover:w-full transition-all duration-500" style={{ backgroundColor: cat.accent }}></div>
                </div>
              </Reveal>
            ))}

            <Reveal delay={0.4}>
              <div onClick={() => setIsContactModalOpen(true)} className="group cursor-pointer bg-blue-950 rounded-2xl overflow-hidden hover:bg-blue-900 transition-all duration-500 hover:-translate-y-1 flex flex-col justify-center p-7 min-h-[200px]">
                <h3 className="text-xl font-bold text-white mb-2">Not sure where to start?</h3>
                <p className="text-white/50 text-sm mb-5">Get a free career assessment and personalized guidance from our experts.</p>
                <div className="flex items-center gap-2 text-red-400 font-semibold text-sm group-hover:gap-3 transition-all duration-300">Get Free Counseling<ArrowUpRight className="w-4 h-4" /></div>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* === ENTRANCE EXAMS === */}
      <section id="exams" className="py-28 bg-white">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 lg:px-10">
          <Reveal>
            <div className="text-center mb-16">
              <div className="flex items-center justify-center gap-3 mb-4">
                <div className="w-10 h-0.5 bg-red-500"></div>
                <span className="text-red-500 text-sm font-semibold tracking-widest uppercase">Entrance Exams</span>
                <div className="w-10 h-0.5 bg-red-500"></div>
              </div>
              <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-blue-950 tracking-tight mb-4">Your Gateway to Success</h2>
              <p className="text-gray-500 text-lg max-w-xl mx-auto">Government & Private entrance exams for UG and PG programs. Click any exam for full details.</p>
            </div>
          </Reveal>

          <div className="space-y-10">
            {Object.entries(examCategories).map(([key, category], catIdx) => (
              <Reveal key={key} delay={catIdx * 0.08}>
                <div className="bg-white rounded-2xl border border-gray-100 overflow-hidden shadow-sm hover:shadow-lg transition-shadow duration-500">
                  <div className="px-7 py-5 border-b border-gray-100 flex items-center justify-between">
                    <div className="flex items-center gap-4">
                      <div className="w-10 h-10 rounded-lg flex items-center justify-center text-white font-bold text-xs tracking-wider" style={{ backgroundColor: category.accent }}>{category.label}</div>
                      <h3 className="text-lg font-bold text-blue-950">{category.title} <span className="font-normal text-gray-400">{category.subtitle}</span></h3>
                    </div>
                    <span className="text-sm text-gray-400 font-medium hidden sm:block">{category.exams.length} exams</span>
                  </div>

                  <div className="p-5">
                    <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
                      {category.exams.map((exam, examIdx) => {
                        const sc = statusColor(exam.status);
                        return (
                          <button key={examIdx} onClick={() => openExamModal(exam, category.title + ' ' + category.subtitle)} className="group text-left p-5 rounded-xl border border-gray-100 hover:border-blue-100 hover:shadow-md transition-all duration-300 hover:-translate-y-0.5 bg-white">
                            <div className="flex items-start justify-between mb-3">
                              <h4 className="font-bold text-blue-950 group-hover:text-red-500 transition-colors duration-300 text-base">{exam.name}</h4>
                              <ArrowUpRight className="w-4 h-4 text-gray-300 group-hover:text-red-500 transition-all duration-300 flex-shrink-0 ml-2" />
                            </div>
                            <p className="text-sm text-gray-500 mb-3">{exam.date}</p>
                            <div className="flex items-center gap-2 flex-wrap">
                              <span className="text-xs font-semibold px-2.5 py-1 rounded-md text-blue-950" style={{ backgroundColor: 'rgba(27,42,91,0.05)' }}>{exam.type}</span>
                              <span className={"text-xs font-semibold px-2.5 py-1 rounded-md flex items-center gap-1.5 " + sc.bg + " " + sc.text}>
                                <span className={"w-1.5 h-1.5 rounded-full " + sc.dot}></span>{exam.status}
                              </span>
                            </div>
                          </button>
                        );
                      })}
                    </div>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* === SUCCESS STORIES === */}
      <section id="success" className="py-28" style={{ backgroundColor: '#FAFBFD' }}>
        <div className="max-w-7xl mx-auto px-5 sm:px-8 lg:px-10">
          <Reveal>
            <div className="text-center mb-16">
              <div className="flex items-center justify-center gap-3 mb-4">
                <div className="w-10 h-0.5 bg-red-500"></div>
                <span className="text-red-500 text-sm font-semibold tracking-widest uppercase">Success Stories</span>
                <div className="w-10 h-0.5 bg-red-500"></div>
              </div>
              <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-blue-950 tracking-tight mb-4">Real Students, Real Achievements</h2>
              <p className="text-gray-500 text-lg max-w-xl mx-auto">Stories that inspire and motivate the next generation of achievers.</p>
            </div>
          </Reveal>

          <div className="grid md:grid-cols-3 gap-6">
            {successStories.map((story, index) => (
              <Reveal key={index} delay={index * 0.12}>
                <div className="bg-white rounded-2xl border border-gray-100 p-8 hover:shadow-xl transition-all duration-500 group h-full flex flex-col">
                  <div className="flex-1">
                    <svg className="w-8 h-8 mb-4" viewBox="0 0 24 24" fill="rgba(232,76,61,0.15)"><path d="M11 7.05V4c-3.36.49-6 3.37-6 6.95 0 3.87 2.69 5.89 4.5 5.89 1.93 0 3.5-1.57 3.5-3.5 0-1.93-1.57-3.5-3.5-3.5-.54 0-1.05.12-1.5.34V7.05zM20 7.05V4c-3.36.49-6 3.37-6 6.95 0 3.87 2.69 5.89 4.5 5.89 1.93 0 3.5-1.57 3.5-3.5 0-1.93-1.57-3.5-3.5-3.5-.54 0-1.05.12-1.5.34V7.05z" /></svg>
                    <p className="text-gray-600 leading-relaxed mb-6" style={{ fontSize: '15px' }}>{story.quote}</p>
                  </div>
                  <div className="pt-6 border-t border-gray-100">
                    <div className="flex items-center gap-4">
                      <div className="w-12 h-12 rounded-full flex items-center justify-center flex-shrink-0" style={{ backgroundColor: 'rgba(27,42,91,0.05)' }}>
                        <span className="text-blue-950 font-bold text-base">{story.name.charAt(0)}</span>
                      </div>
                      <div>
                        <h4 className="font-semibold text-blue-950 text-sm">{story.name}</h4>
                        <p className="text-gray-400 text-xs">{story.achievement}</p>
                      </div>
                    </div>
                    <div className="mt-3">
                      <span className="text-xs font-semibold px-3 py-1 rounded-md text-red-500" style={{ backgroundColor: 'rgba(232,76,61,0.06)' }}>{story.exam}</span>
                    </div>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* === PARTNERSHIP === */}
      <section className="py-28 bg-white">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 lg:px-10">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <Reveal direction="left">
              <div>
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-0.5 bg-red-500"></div>
                  <span className="text-red-500 text-sm font-semibold tracking-widest uppercase">Our Partnership</span>
                </div>
                <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-blue-950 leading-tight mb-6 tracking-tight">Stronger<br /><span className="text-red-500">Together</span></h2>
                <p className="text-gray-600 text-lg leading-relaxed mb-10">A joint venture between a seasoned Career Analyst (Amaze Career Counseling) and an experienced Educationist & Founder (Tesla's Academy) &mdash; united by a shared mission to bridge the gap between academic success and long-term career clarity.</p>
                <div className="space-y-6">
                  {[
                    { title: "Career Discovery", desc: "Guided countless students to discover their true potential and career path." },
                    { title: "Academic Excellence", desc: "Helped students excel in board exams with future-focused vision." },
                    { title: "Future Planning", desc: "Blending academic excellence with career planning for lasting success." }
                  ].map((item, i) => (
                    <div key={i} className="flex gap-4 items-start group">
                      <div className="flex-shrink-0 w-10 h-10 rounded-lg flex items-center justify-center text-red-500 font-bold text-sm group-hover:bg-red-500 group-hover:text-white transition-all duration-300" style={{ backgroundColor: 'rgba(232,76,61,0.06)' }}>{String(i + 1).padStart(2, '0')}</div>
                      <div>
                        <h4 className="font-semibold text-blue-950 mb-0.5">{item.title}</h4>
                        <p className="text-gray-500 text-sm leading-relaxed">{item.desc}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </Reveal>

            <Reveal direction="right" delay={0.15}>
              <div className="bg-blue-950 rounded-2xl p-10 lg:p-14 text-center relative overflow-hidden">
                <div className="absolute inset-0 opacity-5" style={{ backgroundImage: 'radial-gradient(circle at 2px 2px, white 1px, transparent 0)', backgroundSize: '32px 32px' }}></div>
                <div className="relative z-10">
                  <div className="flex justify-center items-center gap-8 mb-10">
                    <div className="text-center">
                      <img src={CareerCompassLogo} className="w-20 h-20 mx-auto" alt="Career Compass" />
                      <h4 className="font-bold text-white text-base mt-3">Career Compass</h4>
                      <p className="text-white/40 text-xs mt-0.5">We Navigate</p>
                    </div>
                    <div className="text-white/20 text-3xl font-light">+</div>
                    <div className="text-center">
                      <img src={TeslaLogo} className="w-20 h-20 mx-auto" alt="Tesla's Academy" />
                      <h4 className="font-bold text-white text-base mt-3">Tesla's Academy</h4>
                      <p className="text-white/40 text-xs mt-0.5">Knowledge is Power</p>
                    </div>
                  </div>
                  <div className="w-16 h-0.5 bg-red-500 mx-auto mb-6"></div>
                  <h3 className="text-2xl font-bold text-white mb-3">Combining Excellence</h3>
                  <p className="text-white/50 leading-relaxed max-w-md mx-auto">Expertise in career counseling meets academic excellence for unprecedented student success.</p>
                </div>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* === BECOME A CERTIFIED CAREER COUNSELOR === */}
      <section id="certification" className="py-28" style={{ backgroundColor: '#FAFBFD' }}>
        <div className="max-w-7xl mx-auto px-5 sm:px-8 lg:px-10">

          {/* Header */}
          <Reveal>
            <div className="text-center mb-16">
              <div className="flex items-center justify-center gap-3 mb-4">
                <div className="w-10 h-0.5 bg-red-500"></div>
                <span className="text-red-500 text-sm font-semibold tracking-widest uppercase">Certification Program</span>
                <div className="w-10 h-0.5 bg-red-500"></div>
              </div>
              <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-blue-950 tracking-tight mb-4">
                Become a Certified<br /><span className="text-red-500">Career Counselor</span>
              </h2>
              <p className="text-gray-500 text-lg max-w-2xl mx-auto">
                Start a new profession, build a meaningful side hustle, or guide your own children confidently &mdash; no B.Ed required.
              </p>
            </div>
          </Reveal>

          {/* Why + Who grid */}
          <div className="grid lg:grid-cols-2 gap-6 mb-10">
            <Reveal delay={0.05}>
              <div className="bg-white rounded-2xl border border-gray-100 p-8 h-full">
                <h3 className="text-lg font-bold text-blue-950 mb-5">Why Career Counseling?</h3>
                <p className="text-gray-500 text-sm leading-relaxed mb-5">
                  Over 95% of Indian students are unaware of diverse career options. New-age careers are emerging faster than schools can adapt, creating massive demand for trained counselors across schools, coaching centers, EdTech platforms, and independent consulting.
                </p>
                <div className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-red-50 text-red-600 text-sm font-semibold">
                  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" /></svg>
                  One of India's fastest-growing professions
                </div>
              </div>
            </Reveal>

            <Reveal delay={0.12}>
              <div className="bg-white rounded-2xl border border-gray-100 p-8 h-full">
                <h3 className="text-lg font-bold text-blue-950 mb-5">Who Is This For?</h3>
                <div className="grid grid-cols-2 gap-3">
                  {['Teachers', 'Graduates / PGs', 'Homemakers restarting careers', 'HR Professionals & Trainers', 'Education enthusiasts', 'Anyone passionate about guiding students'].map((item, i) => (
                    <div key={i} className="flex items-start gap-2.5">
                      <div className="w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5" style={{ backgroundColor: 'rgba(27,42,91,0.06)' }}>
                        <svg className="w-3 h-3 text-blue-950" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}><path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" /></svg>
                      </div>
                      <span className="text-sm text-gray-600">{item}</span>
                    </div>
                  ))}
                </div>
                <p className="text-xs text-gray-400 mt-4 italic">No prior counseling experience required.</p>
              </div>
            </Reveal>
          </div>

          {/* What You Learn + Training Structure */}
          <div className="grid lg:grid-cols-2 gap-6 mb-10">
            <Reveal delay={0.08}>
              <div className="bg-white rounded-2xl border border-gray-100 p-8 h-full">
                <h3 className="text-lg font-bold text-blue-950 mb-5">What You Will Learn</h3>
                <div className="space-y-3">
                  {[
                    'Career Counseling Frameworks',
                    'Psychometric Tools & Interpretation',
                    'Student Profiling & Career Mapping',
                    'Real Case Studies & Practical Scenarios',
                    'Counseling & Communication Skills',
                    'Career Awareness (India & Abroad)'
                  ].map((item, i) => (
                    <div key={i} className="flex items-center gap-3 group">
                      <span className="text-xs font-bold text-gray-300 group-hover:text-red-400 transition-colors w-5">{String(i + 1).padStart(2, '0')}</span>
                      <span className="text-sm text-gray-600 group-hover:text-blue-950 transition-colors">{item}</span>
                    </div>
                  ))}
                </div>
              </div>
            </Reveal>

            <Reveal delay={0.15}>
              <div className="bg-white rounded-2xl border border-gray-100 p-8 h-full">
                <h3 className="text-lg font-bold text-blue-950 mb-5">Training Structure</h3>
                <div className="space-y-4">
                  {[
                    { day: 'Day 1', desc: 'Introduction to Career Counseling Industry' },
                    { day: 'Day 2\u20135', desc: 'Intensive Online Training Sessions' },
                    { day: 'On-demand', desc: 'Recorded Sessions \u2014 Learn at your own pace' },
                    { day: 'Hybrid', desc: 'Online + Practical Exposure model' },
                    { day: 'Post-training', desc: 'Handholding & mentorship support' },
                  ].map((item, i) => (
                    <div key={i} className="flex items-start gap-4">
                      <span className="text-xs font-bold text-red-500 bg-red-50 px-2.5 py-1 rounded-md flex-shrink-0 min-w-[72px] text-center">{item.day}</span>
                      <span className="text-sm text-gray-600 pt-0.5">{item.desc}</span>
                    </div>
                  ))}
                </div>
              </div>
            </Reveal>
          </div>

          {/* Career Opportunities + Start Your Venture */}
          <div className="grid lg:grid-cols-3 gap-6 mb-10">
            <Reveal delay={0.05}>
              <div className="bg-white rounded-2xl border border-gray-100 p-8 h-full">
                <h3 className="text-lg font-bold text-blue-950 mb-4">What Makes Us Different</h3>
                <div className="space-y-3">
                  {['Practical, real-world training', 'Step-by-step counseling process', 'Ready-to-use tools & templates', 'Personal mentorship support', 'Guidance to start your own practice'].map((item, i) => (
                    <div key={i} className="flex items-center gap-2.5">
                      <div className="w-1.5 h-1.5 rounded-full bg-red-400 flex-shrink-0"></div>
                      <span className="text-sm text-gray-600">{item}</span>
                    </div>
                  ))}
                </div>
              </div>
            </Reveal>

            <Reveal delay={0.12}>
              <div className="bg-white rounded-2xl border border-gray-100 p-8 h-full">
                <h3 className="text-lg font-bold text-blue-950 mb-4">Career Opportunities</h3>
                <div className="space-y-3">
                  {['Work in schools (without B.Ed)', 'Join coaching centres / institutions', 'Start your own counseling practice', 'Offer online counseling sessions', 'Build a flexible side income stream'].map((item, i) => (
                    <div key={i} className="flex items-center gap-2.5">
                      <div className="w-1.5 h-1.5 rounded-full bg-emerald-400 flex-shrink-0"></div>
                      <span className="text-sm text-gray-600">{item}</span>
                    </div>
                  ))}
                </div>
                <p className="text-xs text-red-500 font-semibold mt-4">+ Placement support in schools & coaching centres</p>
              </div>
            </Reveal>

            <Reveal delay={0.19}>
              <div className="bg-blue-950 rounded-2xl p-8 h-full text-white relative overflow-hidden">
                <div className="absolute inset-0 opacity-5" style={{ backgroundImage: 'radial-gradient(circle at 2px 2px, white 1px, transparent 0)', backgroundSize: '24px 24px' }}></div>
                <div className="relative z-10">
                  <h3 className="text-lg font-bold mb-4">Start Your Own Venture</h3>
                  <p className="text-white/50 text-sm leading-relaxed mb-5">Go beyond a job. We guide you to build your own career counseling brand.</p>
                  <div className="space-y-3">
                    {['Set up your own counseling brand', 'Conduct career workshops in schools', 'Build parent & student programs', 'Scale into a full-fledged business'].map((item, i) => (
                      <div key={i} className="flex items-center gap-2.5">
                        <div className="w-1.5 h-1.5 rounded-full bg-red-400 flex-shrink-0"></div>
                        <span className="text-sm text-white/70">{item}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </Reveal>
          </div>

          {/* Trainer Profile + CTA */}
          <div className="grid lg:grid-cols-5 gap-6">
            <Reveal delay={0.05} className="lg:col-span-3">
              <div className="bg-white rounded-2xl border border-gray-100 p-8 h-full">
                <div className="flex items-center gap-3 mb-5">
                  <div className="w-14 h-14 rounded-full bg-blue-950 flex items-center justify-center flex-shrink-0">
                    <span className="text-white font-bold text-xl">DS</span>
                  </div>
                  <div>
                    <h3 className="text-lg font-bold text-blue-950">Deepa Subramani</h3>
                    <p className="text-gray-400 text-sm">Senior Career Counselor & Program Lead</p>
                  </div>
                </div>
                <div className="grid sm:grid-cols-2 gap-x-8 gap-y-2 mb-5">
                  {[
                    '10+ years in career guidance & school leadership',
                    'Founder \u2014 Amaze Career Counselling',
                    'Co-Founder \u2014 Career Compass',
                    'CEO \u2014 SCV Central Sr. Sec. School',
                    'Certified Career Analyst (CCA)',
                    'Gold Medalist \u2014 B.Tech Biotechnology'
                  ].map((item, i) => (
                    <div key={i} className="flex items-start gap-2 py-1.5">
                      <div className="w-1.5 h-1.5 rounded-full bg-red-400 flex-shrink-0 mt-1.5"></div>
                      <span className="text-sm text-gray-600">{item}</span>
                    </div>
                  ))}
                </div>
                <div className="flex flex-wrap gap-2">
                  {['Career Pathway Planning', 'Entrance Coaching', 'Scholarship Guidance', 'Parent Awareness Programs'].map((tag, i) => (
                    <span key={i} className="text-xs font-medium px-3 py-1.5 rounded-md bg-gray-50 text-gray-500 border border-gray-100">{tag}</span>
                  ))}
                </div>
                <p className="text-xs text-gray-400 mt-4">Author of <span className="text-blue-950 font-medium">Scholarship Guide</span> & <span className="text-blue-950 font-medium">Career Compass \u2014 Career Pathways Book</span></p>
              </div>
            </Reveal>

            <Reveal delay={0.15} className="lg:col-span-2">
              <div className="bg-red-500 rounded-2xl p-8 h-full text-white flex flex-col justify-between relative overflow-hidden">
                <div className="absolute top-0 right-0 w-40 h-40 bg-white/5 rounded-full -mr-20 -mt-20"></div>
                <div className="relative z-10">
                  <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-md bg-white/15 text-xs font-bold tracking-wider mb-6">LIMITED SEATS</div>
                  <h3 className="text-2xl font-bold mb-3">Shape Careers.<br />Build Your Own.</h3>
                  <p className="text-white/70 text-sm leading-relaxed mb-6">Don't just choose a career &mdash; become the one who guides others. Available in Coimbatore, Avinashi, Tiruppur & Kangeyam.</p>
                  <button onClick={handleCertificationEnquiry} className="w-full py-3.5 bg-white text-red-500 rounded-xl font-bold hover:bg-white/90 transition-all duration-300 flex items-center justify-center gap-2">
                    <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347z" /><path d="M12 0C5.373 0 0 5.373 0 12c0 2.625.846 5.059 2.284 7.034L.789 23.492a.75.75 0 00.917.918l4.462-1.494A11.945 11.945 0 0012 24c6.627 0 12-5.373 12-12S18.627 0 12 0zm0 22c-2.24 0-4.31-.726-5.993-1.955l-.42-.313-2.648.886.886-2.642-.324-.433A9.932 9.932 0 012 12C2 6.477 6.477 2 12 2s10 4.477 10 10-4.477 10-10 10z" /></svg>
                    Register via WhatsApp
                  </button>
                </div>
                <p className="text-white/50 text-xs mt-4 relative z-10 text-center">Call: +91 9626 137 666</p>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* === VIDEO CTA === */}
      <section className="py-28 bg-blue-950 relative overflow-hidden">
        <div className="absolute inset-0 opacity-5" style={{ backgroundImage: 'radial-gradient(circle at 2px 2px, white 1px, transparent 0)', backgroundSize: '40px 40px' }}></div>
        <div className="relative z-10 max-w-4xl mx-auto px-5 sm:px-8 text-center">
          <Reveal>
            <div className="flex items-center justify-center gap-3 mb-4">
              <div className="w-10 h-0.5 bg-red-500"></div>
              <span className="text-red-400 text-sm font-semibold tracking-widest uppercase">Our Story</span>
              <div className="w-10 h-0.5 bg-red-500"></div>
            </div>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white tracking-tight mb-6">Watch Our Journey</h2>
            <p className="text-white/40 text-lg mb-10 max-w-2xl mx-auto">Discover how Career Compass is transforming student lives through personalized guidance and expert coaching.</p>
            <button onClick={() => setIsVideoPlaying(true)} className="group inline-flex items-center gap-3 px-8 py-4 bg-red-500 text-white rounded-full font-semibold hover:bg-red-600 transition-all duration-300 hover:shadow-xl">
              <Play className="w-5 h-5 group-hover:scale-110 transition-transform duration-300" />Watch Our Story
            </button>
          </Reveal>
        </div>
      </section>

      {/* === CONTACT === */}
      <section id="contact" className="py-28 bg-white">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 lg:px-10">
          <div className="grid lg:grid-cols-2 gap-16">
            <Reveal direction="left">
              <div>
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-0.5 bg-red-500"></div>
                  <span className="text-red-500 text-sm font-semibold tracking-widest uppercase">Contact Us</span>
                </div>
                <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-blue-950 leading-tight mb-6 tracking-tight">Ready to Start<br /><span className="text-red-500">Your Journey?</span></h2>
                <p className="text-gray-500 text-lg leading-relaxed mb-10">Contact us today and take the first step towards your dream career. Our experts are here to guide you.</p>
                <div className="space-y-6">
                  {[
                    { Icon: Phone, title: "Call Us", detail: "+91 9626 137 666" },
                    { Icon: Mail, title: "Email Us", detail: "info@careercompass25.com" },
                    { Icon: MapPin, title: "Visit Us", detail: "Coimbatore, Tamil Nadu" }
                  ].map((item, i) => (
                    <div key={i} className="flex items-center gap-5 group">
                      <div className="w-12 h-12 rounded-xl flex items-center justify-center group-hover:bg-red-50 transition-colors duration-300" style={{ backgroundColor: 'rgba(27,42,91,0.04)' }}>
                        <item.Icon className="w-5 h-5 text-blue-950 group-hover:text-red-500 transition-colors duration-300" />
                      </div>
                      <div>
                        <h4 className="font-semibold text-blue-950 text-sm">{item.title}</h4>
                        <p className="text-gray-500">{item.detail}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </Reveal>

            <Reveal direction="right" delay={0.12}>
              <div className="rounded-2xl border border-gray-100 p-8" style={{ backgroundColor: '#FAFBFD' }}>
                <h3 className="text-xl font-bold text-blue-950 mb-2">Office Hours</h3>
                <p className="text-gray-400 text-sm mb-6">Come visit us or reach out anytime during working hours.</p>
                <div className="space-y-4 mb-8">
                  {[
                    { day: "Monday \u2013 Friday", time: "9:00 AM \u2013 7:00 PM" },
                    { day: "Saturday", time: "10:00 AM \u2013 5:00 PM" },
                    { day: "Sunday", time: "Closed" }
                  ].map((s, i) => (
                    <div key={i} className="flex justify-between items-center py-2 border-b border-gray-100 last:border-0">
                      <span className="text-blue-950 font-medium text-sm">{s.day}</span>
                      <span className="text-gray-400 text-sm">{s.time}</span>
                    </div>
                  ))}
                </div>
                <button onClick={() => setIsContactModalOpen(true)} className="w-full py-4 bg-red-500 text-white rounded-xl font-semibold hover:bg-red-600 transition-all duration-300 hover:shadow-lg">Send us a Message</button>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* === FOOTER === */}
      <footer className="bg-blue-950 text-white">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 lg:px-10 py-16">
          <div className="grid md:grid-cols-4 gap-10">
            <div className="md:col-span-1">
              <div className="flex items-center gap-3 mb-5">
                <img src={CareerCompassLogo} className="w-12 h-12" alt="Career Compass" />
                <div>
                  <span className="text-base font-bold text-white">Career Compass</span>
                  <p className="text-xs font-semibold tracking-widest uppercase text-red-400" style={{ fontSize: '10px' }}>We Navigate</p>
                </div>
              </div>
              <p className="text-white/30 text-sm leading-relaxed">Navigate your career journey with confidence and clarity through personalized guidance and expert coaching.</p>
            </div>
            <div>
              <h4 className="text-sm font-bold uppercase tracking-wider text-white/50 mb-5">Quick Links</h4>
              <ul className="space-y-3">
                {[{ label: 'About', id: 'about' }, { label: 'Programs', id: 'programs' }, { label: 'Entrance Exams', id: 'exams' }, { label: 'Certification', id: 'certification' }, { label: 'Success Stories', id: 'success' }, { label: 'Contact', id: 'contact' }].map((link) => (
                  <li key={link.id}><button onClick={() => scrollToSection(link.id)} className="text-white/30 hover:text-white transition-colors duration-300 text-sm">{link.label}</button></li>
                ))}
              </ul>
            </div>
            <div>
              <h4 className="text-sm font-bold uppercase tracking-wider text-white/50 mb-5">Programs</h4>
              <ul className="space-y-3 text-white/30 text-sm">
                {['Engineering', 'Design & Architecture', 'Management', 'Law', 'Arts & Science'].map((p) => (<li key={p}>{p}</li>))}
              </ul>
            </div>
            <div>
              <h4 className="text-sm font-bold uppercase tracking-wider text-white/50 mb-5">Contact Info</h4>
              <ul className="space-y-3 text-white/30 text-sm">
                <li>+91 9626 137 666</li>
                <li>info@careercompass25.com</li>
                <li>Coimbatore, Tamil Nadu</li>
              </ul>
            </div>
          </div>
          <div className="border-t border-white/10 mt-12 pt-8 text-center">
            <p className="text-white/20 text-sm">&copy; 2025 Career Compass. All rights reserved.</p>
          </div>
        </div>
      </footer>

      {/* === FLOATING CTA === */}
      <button onClick={() => setIsContactModalOpen(true)} className="fixed bottom-6 right-6 z-40 w-14 h-14 bg-red-500 text-white rounded-full shadow-xl hover:shadow-2xl hover:scale-105 transition-all duration-300 flex items-center justify-center" aria-label="Contact us">
        <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" /></svg>
      </button>

      {/* === CONTACT MODAL === */}
      {isContactModalOpen && (
        <div className="fixed inset-0 flex items-center justify-center z-50 p-4" style={{ backgroundColor: 'rgba(27,42,91,0.6)', backdropFilter: 'blur(4px)' }} onClick={(e) => { if (e.target === e.currentTarget) setIsContactModalOpen(false); }}>
          <div className="bg-white rounded-2xl w-full max-w-md overflow-hidden shadow-2xl" style={{ animation: 'modalIn 0.3s ease-out' }}>
            <div className="bg-blue-950 px-7 py-5 flex items-center justify-between">
              <div>
                <h3 className="text-lg font-bold text-white">Get in Touch</h3>
                <p className="text-white/40 text-xs mt-0.5">Fill in your details and we'll connect via WhatsApp</p>
              </div>
              <button onClick={() => setIsContactModalOpen(false)} className="text-white/40 hover:text-white transition-colors p-1"><X className="w-5 h-5" /></button>
            </div>
            <div className="p-7">
              <FormFields onSubmit={handleSubmit} buttonText="Send via WhatsApp" />
            </div>
          </div>
        </div>
      )}

      {/* === EXAM DETAIL MODAL === */}
      {isExamModalOpen && selectedExam && (
        <div className="fixed inset-0 flex items-center justify-center z-50 p-4" style={{ backgroundColor: 'rgba(27,42,91,0.6)', backdropFilter: 'blur(4px)' }} onClick={(e) => { if (e.target === e.currentTarget) setIsExamModalOpen(false); }}>
          <div className="bg-white rounded-2xl w-full max-w-lg overflow-hidden shadow-2xl flex flex-col" style={{ animation: 'modalIn 0.3s ease-out', maxHeight: '90vh' }}>
            <div className="bg-blue-950 px-7 py-5 flex-shrink-0">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-red-400 text-xs font-semibold tracking-widest uppercase mb-1">{selectedExamCategory}</p>
                  <h3 className="text-xl font-bold text-white">{selectedExam.name}</h3>
                </div>
                <button onClick={() => setIsExamModalOpen(false)} className="text-white/40 hover:text-white transition-colors p-1"><X className="w-5 h-5" /></button>
              </div>
              <div className="flex items-center gap-2 mt-3">
                <span className="text-xs font-semibold px-2.5 py-1 rounded-md bg-white/10 text-white">{selectedExam.type}</span>
                {(() => { const sc = statusColor(selectedExam.status); return (<span className={"text-xs font-semibold px-2.5 py-1 rounded-md flex items-center gap-1.5 " + sc.bg + " " + sc.text}><span className={"w-1.5 h-1.5 rounded-full " + sc.dot}></span>{selectedExam.status}</span>); })()}
                <span className="text-xs text-white/40 ml-auto">{selectedExam.date}</span>
              </div>
            </div>

            <div className="overflow-y-auto flex-1 p-7">
              {examDetails[selectedExam.name] ? (
                <div className="space-y-4 mb-8">
                  {[
                    { label: "Full Name", value: examDetails[selectedExam.name].fullName },
                    { label: "Conducted By", value: examDetails[selectedExam.name].conductedBy },
                    { label: "Eligibility", value: examDetails[selectedExam.name].eligibility },
                    { label: "Exam Pattern", value: examDetails[selectedExam.name].pattern },
                    { label: "Top Colleges", value: examDetails[selectedExam.name].topColleges },
                    { label: "Syllabus", value: examDetails[selectedExam.name].syllabus },
                    { label: "Attempts", value: examDetails[selectedExam.name].attempts },
                    { label: "Mode", value: examDetails[selectedExam.name].mode },
                  ].map((item, i) => (
                    <div key={i} className="border-b border-gray-100 pb-3 last:border-0 last:pb-0">
                      <p className="font-bold tracking-wider uppercase text-gray-400 mb-1" style={{ fontSize: '10px' }}>{item.label}</p>
                      <p className="text-blue-950 text-sm leading-relaxed">{item.value}</p>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-gray-400 text-sm mb-8">Detailed information coming soon. Enquire below for personalized guidance.</p>
              )}

              <div className="rounded-xl p-5 border border-gray-100" style={{ backgroundColor: '#FAFBFD' }}>
                <h4 className="font-bold text-blue-950 text-sm mb-4">Enquire about {selectedExam.name}</h4>
                <FormFields onSubmit={handleExamEnquiry} buttonText="Enquire via WhatsApp" />
              </div>
            </div>
          </div>
        </div>
      )}

      {/* === VIDEO MODAL === */}
      {isVideoPlaying && (
        <div className="fixed inset-0 bg-black/90 flex items-center justify-center z-50 p-4" onClick={() => setIsVideoPlaying(false)}>
          <div className="relative w-full max-w-5xl" onClick={(e) => e.stopPropagation()}>
            <button onClick={() => setIsVideoPlaying(false)} className="absolute -top-12 right-0 text-white/60 hover:text-white transition-colors"><X className="w-7 h-7" /></button>
            <div className="bg-blue-950 rounded-2xl overflow-hidden aspect-video flex items-center justify-center">
              <div className="text-center text-white">
                <Play className="w-16 h-16 mx-auto mb-4 text-white/30" />
                <h3 className="text-xl font-bold mb-1">Career Compass Success Stories</h3>
                <p className="text-white/40 text-sm">Video player will be embedded here</p>
              </div>
            </div>
          </div>
        </div>
      )}

      <style>{"\n@keyframes modalIn {\n  from { opacity: 0; transform: translateY(16px) scale(0.97); }\n  to { opacity: 1; transform: translateY(0) scale(1); }\n}\nhtml { scroll-behavior: smooth; }\n::selection { background: rgba(232,76,61,0.13); color: #1B2A5B; }\n* { -webkit-font-smoothing: antialiased; -moz-osx-font-smoothing: grayscale; }\n"}</style>
    </div>
  );
};

export default CareerCompassWebsite;
